import React, { useMemo, useState } from "react";
import { Box, Typography, Tooltip as MuiTooltip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { summaryCardTheme as t } from "./styles";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";

function SummaryFrameworkComplianceChart({ overall, framework, onFrameworkChange }) {
  const [expandedFramework, setExpandedFramework] = useState(null);
  const data = useMemo(() => {
    const list = Array.isArray(overall?.compliance_by_framework)
      ? overall.compliance_by_framework
      : [];

    return list
      .filter((fw) => (fw.controls_failed_total ?? 0) > 0)
      .map((fw) => ({
        name: fw.framework || "—",
        arns: fw.arns_failed_total ?? 0,
        controls: fw.controls_failed_total ?? 0,
        failedControlIds: Array.isArray(fw.failed_control_ids)
          ? fw.failed_control_ids
          : [],
      }))
      .sort((a, b) => b.controls - a.controls);
  }, [overall]);

  const maxLegendItems = 6;

  const sliceColors = useMemo(() => {
    const base = t.colors.chart || [];
    const fallback = ["#6366F1", "#F97316", "#22C55E", "#EC4899", "#06B6D4", "#FACC15"];
    return data.map((_, idx) => base[idx % base.length] || fallback[idx % fallback.length]);
  }, [data]);

  const CustomYAxisTick = ({ x, y, payload }) => {
    const name = payload?.value;
    const iconSrc = name ? getComplianceIcon(name) : null;

    // Render logos in the Y-axis to avoid long framework names pushing/obscuring tooltips.
    return (
      <g transform={`translate(${x},${y})`}>
        {iconSrc ? (
          <image
            href={iconSrc}
            x={-18}
            y={-9}
            width={18}
            height={18}
            preserveAspectRatio="xMidYMid meet"
          />
        ) : (
          <text
            x={0}
            y={0}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={10}
            fill={t.colors.text}
          >
            —
          </text>
        )}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;

    const iconSrc = getComplianceIcon(d.name);

    // Hover-only: show brief metrics so tooltip can disappear without affecting interaction.
    return (
      <Box
        sx={{
          ...t.chartTooltip,
          fontFamily: t.fontFamily,
          // Prevent horizontal overflow; adapt to screen size.
          maxWidth: "min(260px, 80vw)",
          p: 1.25,
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.25,
          }}
        >
          {iconSrc && (
            <Box
              component="img"
              src={iconSrc}
              alt={d.name}
              sx={{
                width: 18,
                height: 18,
                borderRadius: 1,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          )}
          <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.78rem" }}>
            {d.name}
          </Typography>
        </Box>
        <Typography component="div" sx={{ color: t.colors.textMuted, fontSize: "0.73rem" }}>
          {d.arns} ARNs with findings · {d.controls} failed controls
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No compliance framework findings
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        fontFamily: t.fontFamily,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: t.colors.text,
          fontSize: "0.875rem",
          mb: 1,
          fontFamily: t.fontFamily,
        }}
      >
        Compliance Frameworks
      </Typography>
      {/* Scrollable horizontal bar chart so many frameworks stay readable */}
      <Box
        sx={{
          mt: 0.5,
          maxHeight: 220,
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Box sx={{ minHeight: 160, height: Math.max(160, data.length * 26) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 12, left: 22, bottom: 4 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke={t.colors.borderLight}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: t.colors.textMuted }}
                tickFormatter={(v) =>
                  v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                }
              />
              <YAxis
                type="category"
                dataKey="name"
                width={56}
                tick={<CustomYAxisTick />}
                interval={0}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
              <Bar
                dataKey="controls"
                radius={[0, 4, 4, 0]}
                maxBarSize={18}
                onClick={(entry) => {
                  const clickedName = entry?.name;
                  if (!clickedName) return;

                  if (onFrameworkChange) {
                    onFrameworkChange(
                      framework && framework === clickedName ? "" : clickedName
                    );
                  }

                  setExpandedFramework((prev) =>
                    prev && prev.name === clickedName ? null : entry
                  );
                }}
              >
                {data.map((entry, index) => {
                  const isActive = framework && framework === entry.name;
                  return (
                    <Cell
                      key={`bar-${entry.name}-${index}`}
                      fill={sliceColors[index]}
                      stroke={isActive ? t.colors.accent : "transparent"}
                      strokeWidth={isActive ? 2 : 0}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {expandedFramework && (
        <Box
          sx={{
            mt: 1.5,
            borderRadius: t.radius.lg,
            border: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
            boxShadow: t.shadow,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${t.colors.border}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getComplianceIcon(expandedFramework.name) && (
                <Box
                  component="img"
                  src={getComplianceIcon(expandedFramework.name)}
                  alt={expandedFramework.name}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 1,
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              )}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: t.colors.text,
                  }}
                >
                  Framework
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: t.colors.textMuted,
                  }}
                >
                  {expandedFramework.arns} ARNs with findings ·{" "}
                  {expandedFramework.controls} failed controls
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setExpandedFramework(null)}
              sx={{
                color: t.colors.textMuted,
                "&:hover": {
                  color: t.colors.text,
                  backgroundColor: t.colors.borderLight,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {Array.isArray(expandedFramework.failedControlIds) &&
            expandedFramework.failedControlIds.length > 0 && (
              <Box
                sx={{
                  maxHeight: 220,
                  overflowY: "auto",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: t.colors.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.5,
                  }}
                >
                  Control IDs
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: t.colors.text,
                    wordBreak: "break-word",
                  }}
                >
                  {expandedFramework.failedControlIds.join(", ")}
                </Typography>
              </Box>
            )}
        </Box>
      )}
    </Box>
  );
}

export default SummaryFrameworkComplianceChart;

